/** What the bot wants: diamonds until the quota, then the exit; keys and clocks when handy. */

import { E } from '../sim';
import type { CaveState } from '../sim';
import { INF as UNREACHED } from './paths';
import type { Paths } from './paths';

// Local copies: these run for every cell of the cave, every scan.
const { STEEL, DIAMOND, BOULDER, CLOCK, KEY, OUTBOX_OPEN } = E;
const INF = UNREACHED;

/** A new target must beat the current one by more than this to take over (no dithering). */
const KEEP_MARGIN = 4;
/** A diamond under a boulder pins Digby beneath it for a moment: slightly less attractive. */
const UNDER_BOULDER = 3;
/** Clocks are a bonus, not the job (Deep). */
const CLOCK_COST = 8;

/** Is `el` something to head for right now? */
export function isTarget(el: number, gateOpen: boolean): boolean {
  if (gateOpen) return el === OUTBOX_OPEN;
  return el === DIAMOND || el === CLOCK || (el >= KEY && el < KEY + 3);
}

/**
 * The best reachable target, or -1. `keep` (the current target, or -1) stays chosen unless another
 * is clearly better, so Digby does not dither between two diamonds the same distance away. With
 * the gate open but no way to the exit, keys are still worth having: a door may be in the way.
 */
export function pickTarget(s: CaveState, near: Paths, banned: Int32Array, keep: number): number {
  const best = bestOf(s, near, banned, keep, s.gateOpen);
  return best < 0 && s.gateOpen ? bestOf(s, near, banned, keep, false, true) : best;
}

function bestOf(s: CaveState, near: Paths, banned: Int32Array, keep: number, gateOpen: boolean, keysOnly = false): number {
  let best = -1;
  let bestScore = INF;
  const cells = s.cells;
  const score = (c: number): number => {
    const el = cells[c] ?? STEEL;
    if (!isTarget(el, gateOpen) || (keysOnly && (el < KEY || el >= KEY + 3))) return INF;
    const d = near.dist[c] ?? INF;
    if (d >= INF) return INF;
    if (el === DIAMOND && c >= s.w && cells[c - s.w] === BOULDER) return d + UNDER_BOULDER;
    if (el === CLOCK) return d + (s.timeLeft < 30 ? 0 : CLOCK_COST);
    return d;
  };
  for (let c = 0; c < cells.length; c++) {
    if ((banned[c] ?? 0) > s.scan) continue;
    const v = score(c);
    if (v < bestScore) {
      bestScore = v;
      best = c;
    }
  }
  if (keep >= 0 && keep !== best && (banned[keep] ?? 0) <= s.scan) {
    const kept = score(keep);
    if (kept < INF && kept <= bestScore + KEEP_MARGIN) return keep;
  }
  return best;
}
